


























































import Vue, { VueConstructor } from 'vue'
import moment from 'moment'
import Clickoutside from 'element-ui/src/utils/clickoutside'

import ActionSheet from '@/components/global/action-sheet.vue'
import DdButton from '@/components/global/buttons/dd-button.vue'

export interface IDatePicker {
  $refs: {
    datePicker: {
      handleFocus(): void
      handleClose(): void
    }
  }
}

/**
 * Due date picker component
 */

export default (Vue as VueConstructor<Vue & IDatePicker>).extend({
  name: 'DueDatePicker',

  directives: { Clickoutside },

  components: {
    ActionSheet,
    DdButton,
  },

  props: {
    value: {
      type: String,
      default: null,
    },
    /* By default, hitting "Clear due date" changes the value to null.
       Instead, this will emit the event 'clear-due-date-clicked' */
    emitEventOnClear: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: 'small',
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    let firstDayOfWeek = 7
    try {
      // @ts-ignore
      firstDayOfWeek = new Intl.Locale(navigator.language).weekInfo.firstDay
    } catch {
      try {
        firstDayOfWeek = moment.localeData(navigator.language).firstDayOfWeek()
      } catch {
        // ignore
      }
    }

    return {
      selectedDate: null,
      formattedDate: '',
      calendarVisible: false,
      calendarVisibleEvents: false,
      options: {
        firstDayOfWeek,
      },
    }
  },

  watch: {
    selectedDate(newVal: string) {
      this.formattedDate = newVal
        ? moment(newVal).format(this.getFormattedDate(newVal))
        : ''

      if (newVal !== this.value) {
        // Always emit a timezone-agnostic date from here...
        this.$emit('input', newVal ? moment(newVal).format('YYYY-MM-DD') : null)
        this.calendarVisible = false
      }
    },

    value: {
      immediate: true,
      handler(value) {
        const date = value
          ? moment(value).format(this.getFormattedDate(value))
          : ''

        if (date !== this.formattedDate || !this.selectedDate) {
          this.selectedDate = value
        }
      },
    },

    formattedDate: {
      immediate: true,
      handler() {
        if (this.selectedDate) {
          this.formattedDate = moment(this.selectedDate).format(
            this.getFormattedDate(this.selectedDate),
          )
        } else {
          return ''
        }
      },
    },

    async calendarVisible(value: boolean) {
      if (value) {
        await this.$nextTick()
        this.addClearDueDateToFooter()
      }
    },
  },

  computed: {
    slotHydrated(): boolean {
      /* tslint:disable:no-string-literal */
      return !!this.$slots['button']
    },
    textToDisplay(): string {
      return this.selectedDate
        ? `&nbsp;&nbsp;<strong>${this.formattedDate}</strong>`
        : ''
    },
    hasSelectedDate(): boolean {
      return this.selectedDate !== null
    },
    buttonTitle(): string {
      if (this.formattedDate) {
        return `Due: ${this.formattedDate}`
      }

      return `No due date`
    },
  },

  mounted() {
    const suffixInner: HTMLElement = this.$el.querySelector(
      '.el-input__suffix-inner',
    )
    if (suffixInner) {
      suffixInner.innerHTML = `<i class="ddi-caret-up ddi"></i>`
    }
  },

  methods: {
    /**
     * Call focus event on hidden input to show calendar dropdown picker
     */
    toggleCalendar() {
      if (this.calendarVisibleEvents && this.calendarVisible) {
        this.$refs.datePicker.handleClose()
      } else if (!this.calendarVisible) {
        this.$refs.datePicker.handleFocus()
        this.calendarVisible = true
      } else if (this.calendarVisible) {
        this.calendarVisible = false
      }
    },

    addClearDueDateToFooter() {
      const footer: HTMLElement = this.$el.querySelector(
        '.el-picker-panel__footer',
      )

      footer.innerHTML = `<button
        id="clearButton"
        type="text"
        color="blue"
        class="clear-button">Clear due date</button>`
      footer
        .querySelector('#clearButton')
        .addEventListener('click', (event: Event) => {
          if (this.emitEventOnClear) {
            this.$emit('clear-due-date-clicked')
          } else {
            this.$emit('input', null)
          }
          this.handleClose()
          event.preventDefault()
        })
    },

    handleClose() {
      this.$refs.datePicker.handleClose()
      this.calendarVisible = false
    },

    /**
     * Handle events from el-date-picker
     */
    handleVisibility() {
      // @ts-ignore
      this.calendarVisibleEvents = this.$refs.datePicker.pickerVisible
      if (this.$screen.isMobile(this.$mq)) {
        if (!this.calendarVisibleEvents && this.calendarVisible) {
          this.$refs.datePicker.handleFocus()
        }
      }
    },

    /**
     * Return string for formated date
     */
    getFormattedDate(value: string): string {
      const currentDate = moment(new Date())
      return moment(value).isSame(currentDate, 'year') ? 'MMM D' : 'MMM D ’YY'
    },
  },
})
